import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Calculator, Heart, MessageCircle, LineChart, TrendingUp, Truck, Search, Home, Loader2, Send, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { submitEnquiry, getAreaSoldPrices, createSavedSearch } from "../api";
import { useToast } from "../hooks/use-toast";

const Arch = ({ size = 34 }) => (
  <svg width={size} height={(size * 22) / 30} viewBox="0 0 30 22" fill="none">
    <path d="M2 20 C2 9 8 3 15 3 C22 3 28 9 28 20" stroke="#b8935f" strokeWidth="1.8" fill="none" strokeLinecap="round" />
    <path d="M7 20 C7 12 10 8 15 8 C20 8 23 12 23 20" stroke="#b8935f" strokeWidth="1.3" fill="none" strokeLinecap="round" opacity="0.7" />
    <line x1="15" y1="1" x2="15" y2="6" stroke="#b8935f" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const fmt = (n) => (n || n === 0 ? `\u00A3${Number(n).toLocaleString()}` : "\u2014");
const fieldCls = "w-full h-[46px] px-3.5 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]";
const areaCls = "w-full px-3.5 py-2.5 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f] resize-none";
const submitBtn = "w-full h-[50px] rounded-lg bg-[#b8935f] text-white text-[15px] font-500 hover:bg-[#a67f4d] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2";

// ---------- Lead-capture form (Find a Property / Moving Concierge / Investor) ----------
const LeadForm = ({ config, onDone }) => {
  const { toast } = useToast();
  const [f, setF] = useState({ name: "", email: "", phone: "", detail: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email) { toast({ title: "Name and email are required" }); return; }
    setBusy(true);
    try {
      await submitEnquiry({ name: f.name, email: f.email, phone: f.phone, message: `${config.label}: ${f.detail}`, source: config.source });
      toast({ title: config.success, description: "Our team will be in touch very soon." });
      onDone();
    } catch { toast({ title: "Something went wrong", description: "Please try again." }); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid={`actionhub-form-${config.source}`}>
      <p className="text-[13px] text-[#6a6a6a] -mt-1 mb-1">{config.blurb}</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Full name" className={fieldCls} data-testid="actionhub-name" />
        <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Phone (optional)" className={fieldCls} />
      </div>
      <input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email address" className={fieldCls} data-testid="actionhub-email" />
      <textarea value={f.detail} onChange={(e) => setF({ ...f, detail: e.target.value })} rows={3} placeholder={config.placeholder} className={areaCls} data-testid="actionhub-detail" />
      <button type="submit" disabled={busy} className={submitBtn} data-testid="actionhub-submit">{busy ? <Loader2 size={17} className="animate-spin" /> : <><Send size={16} /> {config.cta}</>}</button>
    </form>
  );
};

// ---------- Saved Search (Find Me a Property → alerts) ----------
const SavedSearchForm = ({ onDone }) => {
  const { toast } = useToast();
  const [f, setF] = useState({ name: "", email: "", phone: "", area: "", lookingFor: "buy", minBeds: "", maxPrice: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email) { toast({ title: "Name and email are required" }); return; }
    setBusy(true);
    try {
      await createSavedSearch({ ...f, minBeds: Number(f.minBeds) || 0, maxPrice: Number(f.maxPrice) || 0 });
      toast({ title: "Search saved", description: "We'll email you the moment a matching home lists." });
      onDone();
    } catch { toast({ title: "Something went wrong", description: "Please try again." }); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="actionhub-saved-search">
      <p className="text-[13px] text-[#6a6a6a] -mt-1 mb-1">Tell us what you're after — we'll ping you the instant a matching home comes to market, often before the portals.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Full name" className={fieldCls} data-testid="actionhub-name" />
        <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Phone (optional)" className={fieldCls} />
      </div>
      <input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email address" className={fieldCls} data-testid="actionhub-email" />
      <div className="grid grid-cols-2 gap-3">
        <select value={f.lookingFor} onChange={(e) => setF({ ...f, lookingFor: e.target.value })} className={fieldCls} data-testid="ss-lookingfor"><option value="buy">Buying</option><option value="rent">Renting</option></select>
        <input value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })} placeholder="Area / postcode (e.g. Bath, BA2)" className={fieldCls} data-testid="ss-area" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <input value={f.minBeds} onChange={(e) => setF({ ...f, minBeds: e.target.value.replace(/[^0-9]/g, "") })} placeholder="Min bedrooms" className={fieldCls} data-testid="ss-beds" />
        <input value={f.maxPrice} onChange={(e) => setF({ ...f, maxPrice: e.target.value.replace(/[^0-9]/g, "") })} placeholder="Max price £" className={fieldCls} data-testid="ss-price" />
      </div>
      <textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} placeholder="Anything else — garden, parking, must-haves…" className={areaCls} />
      <button type="submit" disabled={busy} className={submitBtn} data-testid="actionhub-submit">{busy ? <Loader2 size={17} className="animate-spin" /> : <><Send size={16} /> Create my property alert</>}</button>
    </form>
  );
};

// ---------- Market Insight tool ----------
const MarketInsight = () => {
  const { toast } = useToast();
  const [pc, setPc] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const run = async (e) => {
    e.preventDefault();
    if (!pc.trim()) { toast({ title: "Enter a town or postcode" }); return; }
    setBusy(true);
    try { setData(await getAreaSoldPrices(pc.trim())); }
    catch { toast({ title: "Lookup failed", description: "Try a valid UK postcode." }); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="actionhub-market-insight">
      <p className="text-[13px] text-[#6a6a6a] -mt-1">See recent sold prices and the market average for any town or postcode across Bath, Bristol & beyond.</p>
      <form onSubmit={run} className="flex gap-2">
        <input value={pc} onChange={(e) => setPc(e.target.value)} placeholder="Town or postcode (e.g. BA1, Clifton)" className={fieldCls} data-testid="market-insight-input" />
        <button type="submit" disabled={busy} className="px-5 rounded-lg bg-[#b8935f] text-white text-[14px] font-500 hover:bg-[#a67f4d] transition-colors disabled:opacity-60 inline-flex items-center gap-2" data-testid="market-insight-btn">{busy ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Search</button>
      </form>
      {data && (
        <div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-[#faf7f2] rounded-xl border border-[#f0ece5] p-4"><p className="text-[11px] text-[#a0a0a0] uppercase">Median sold</p><p className="font-display text-[22px] font-500 text-[#2b2b2b]">{fmt(data.median)}</p></div>
            <div className="bg-[#faf7f2] rounded-xl border border-[#f0ece5] p-4"><p className="text-[11px] text-[#a0a0a0] uppercase">Area average</p><p className="font-display text-[22px] font-500 text-[#2b2b2b]">{fmt(data.average)}</p></div>
          </div>
          {data.transactions?.length > 0 ? (
            <div className="space-y-2 max-h-[220px] overflow-y-auto">
              {data.transactions.map((t, i) => (
                <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-[#faf7f2] border border-[#f0ece5] text-[13px]">
                  <span className="text-[#2b2b2b] font-500 truncate pr-2">{t.address}</span>
                  <span className="text-[#6a6a6a] whitespace-nowrap">{fmt(t.price)}{t.date ? ` \u00B7 ${t.date}` : ""}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-[13px] text-[#8a8a8a]">No recent sold records found. {data.sources?.length ? "" : "Try a fuller postcode."}</p>}
          <p className="text-[11px] text-[#a0a0a0] mt-3">Source: {(data.sources || []).join(" · ") || "HM Land Registry"}</p>
        </div>
      )}
    </div>
  );
};

const FORMS = {
  moving: { label: "Moving concierge", source: "moving-concierge", blurb: "From removals to utilities and cleaning — our concierge handles the move for you.", placeholder: "Moving date, from/to areas, what you'd like help with…", cta: "Request concierge", success: "Concierge request sent" },
  investor: { label: "Investor sign-up", source: "investor", blurb: "Get off-market deals, yield reports and new-development launches straight to your inbox.", placeholder: "Budget, target yield, preferred areas, cash or mortgage…", cta: "Join investor list", success: "You're on the investor list" },
};

const ActionHub = () => {
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState(null); // 'find' | 'moving' | 'investor' | 'market'
  const navigate = useNavigate();

  const items = [
    { key: "valuation", label: "Instant Valuation", icon: Home, run: () => navigate("/#valuation") },
    { key: "find", label: "Find Me a Property", icon: Search, run: () => setModal("find") },
    { key: "market", label: "Market Insight", icon: LineChart, run: () => setModal("market") },
    { key: "investor", label: "Investor Sign-up", icon: TrendingUp, run: () => setModal("investor") },
    { key: "moving", label: "Moving Concierge", icon: Truck, run: () => setModal("moving") },
    { key: "saved", label: "Saved Homes", icon: Heart, run: () => navigate("/favourites") },
    { key: "calc", label: "Calculators", icon: Calculator, run: () => navigate("/calculators") },
    { key: "concierge", label: "Anadear Concierge", icon: MessageCircle, run: () => window.dispatchEvent(new CustomEvent("anadear:open-concierge")) },
  ];
  const act = (it) => { it.run(); setOpen(false); };

  const titles = { find: "Find Me a Property", moving: "Moving Concierge", investor: "Investor Sign-up", market: "Market Insight" };

  return (
    <>
      <div className="fixed bottom-6 left-6 z-40 flex flex-col items-start gap-3" data-testid="action-hub">
        {open && (
          <div className="flex flex-col gap-2.5 mb-1">
            {items.map((it, i) => {
              const Icon = it.icon;
              return (
                <button key={it.key} onClick={() => act(it)} data-testid={`action-hub-${it.key}`} style={{ animationDelay: `${i * 40}ms` }}
                  className="group flex items-center gap-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <span className="w-11 h-11 rounded-full bg-white shadow-[0_6px_20px_rgba(0,0,0,0.15)] border border-[#f0ece5] flex items-center justify-center group-hover:scale-110 transition-transform text-[#b8935f]">
                    <Icon size={19} />
                  </span>
                  <span className="px-3 py-1.5 rounded-lg bg-[#1c1c1e] text-white text-[13px] font-500 whitespace-nowrap shadow-lg">{it.label}</span>
                </button>
              );
            })}
          </div>
        )}
        <button onClick={() => setOpen((o) => !o)} aria-label="Quick actions" data-testid="action-hub-toggle"
          className={`w-14 h-14 rounded-full flex items-center justify-center shadow-[0_8px_24px_rgba(184,147,95,0.55)] transition-all duration-300 ring-2 ring-offset-2 ring-offset-[#faf7f2] ${open ? "bg-[#1c1c1e] ring-[#1c1c1e]" : "bg-white hover:scale-105 border-2 border-[#b8935f] ring-[#b8935f]/30"}`}>
          {open ? <X size={24} className="text-white" /> : <Arch />}
        </button>
      </div>

      <Dialog open={Boolean(modal)} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogTitle className="font-display text-[24px] font-300 text-[#2b2b2b] mb-3">{titles[modal]}</DialogTitle>
          {modal === "market" ? <MarketInsight /> : modal === "find" ? <SavedSearchForm onDone={() => setModal(null)} /> : modal && <LeadForm config={FORMS[modal]} onDone={() => setModal(null)} />}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ActionHub;
